/**
 * MODULE: _shared/frontend-shell/src/features/crm/components/shared/OwnerAvatar.tsx
 * PURPOSE: CRM owner circle — image or initials, optional name beside
 * DEPENDENCIES: react
 * PUBLIC_API: OwnerAvatar, OwnerAvatarProps
 * SIDE_EFFECTS: None
 * AGENT_SAFETY: SAFE
 * FILE_SIZE: ~85/200
 */

import { useState } from 'react';

export type OwnerAvatarProps = {
  name: string;
  avatarUrl?: string;
  size?: 'xs' | 'sm' | 'md';
  showName?: boolean;
};

function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const SIZE_MAP: Record<NonNullable<OwnerAvatarProps['size']>, { box: string; text: string }> = {
  xs: { box: 'h-6 w-6 min-h-6 min-w-6 text-[10px]', text: 'text-xs' },
  sm: { box: 'h-8 w-8 min-h-8 min-w-8 text-xs', text: 'text-sm' },
  md: { box: 'h-10 w-10 min-h-10 min-w-10 text-sm', text: 'text-base' },
};

export function OwnerAvatar({ name, avatarUrl, size = 'sm', showName = false }: OwnerAvatarProps) {
  const [imgFailed, setImgFailed] = useState(false);
  const { box, text } = SIZE_MAP[size];
  const initials = initialsFromName(name);
  const showImg = Boolean(avatarUrl) && !imgFailed;

  const circle = (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-200 bg-gray-100 font-semibold uppercase text-gray-700 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 ${box}`}
      data-testid="owner-avatar-circle"
    >
      {showImg ? (
        <img
          src={avatarUrl}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span aria-hidden>{initials}</span>
      )}
    </span>
  );

  if (showName) {
    return (
      <span className={`inline-flex max-w-full items-center gap-2 ${text}`} data-testid="owner-avatar">
        {circle}
        <span className="min-w-0 truncate font-medium text-gray-900 dark:text-white">{name}</span>
      </span>
    );
  }

  return (
    <span className="inline-flex items-center" data-testid="owner-avatar" title={name}>
      {circle}
      <span className="sr-only">{name}</span>
    </span>
  );
}
